import { Controller } from "@hotwired/stimulus"

type ValidatableField = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

export default class extends Controller {
  static targets = ["step", "indicator", "previous", "next", "submit"]
  static values = { initialIndex: Number }

  declare readonly stepTargets: HTMLElement[]
  declare readonly indicatorTargets: HTMLElement[]
  declare readonly previousTarget: HTMLElement
  declare readonly nextTarget: HTMLElement
  declare readonly submitTarget: HTMLElement
  declare readonly initialIndexValue: number
  private index = 0

  connect() {
    this.updateConditionalRequirements()
    if (this.stepTargets.length === 0) return

    this.index = Math.min(Math.max(this.initialIndexValue, 0), this.stepTargets.length - 1)
    this.render({ scroll: false })
  }

  next() {
    if (!this.validateStep(this.stepTargets[this.index])) return

    if (this.index < this.stepTargets.length - 1) this.index += 1
    this.render()
  }

  submit(event: SubmitEvent) {
    this.updateConditionalRequirements()
    const invalidIndex = this.stepTargets.findIndex((step) => !this.stepIsValid(step))
    if (invalidIndex < 0) return

    event.preventDefault()
    this.index = invalidIndex
    this.render()
    this.validateStep(this.stepTargets[invalidIndex])
  }

  updateConditionalRequirements() {
    this.element.querySelectorAll<HTMLElement>("[data-validate-required-if]").forEach((field) => {
      const condition = field.dataset.validateRequiredIf
      if (!condition) return

      const separator = condition.lastIndexOf(":")
      if (separator < 0) return

      const selector = condition.slice(0, separator)
      const expectedValue = condition.slice(separator + 1)
      const source = this.element.querySelector<HTMLInputElement | HTMLSelectElement>(selector)
      if (source) field.toggleAttribute("required", source.value === expectedValue)
    })
  }

  previous() {
    if (this.index > 0) this.index -= 1
    this.render()
  }

  private render({ scroll = true } = {}) {
    this.stepTargets.forEach((step, index) => step.classList.toggle("hidden", index !== this.index))
    this.dispatch("step-shown", { detail: { step: this.stepTargets[this.index] } })
    this.indicatorTargets.forEach((indicator, index) => {
      indicator.classList.toggle("is-active", index === this.index)
    })
    this.previousTarget.classList.toggle("hidden", this.index === 0)
    this.nextTarget.classList.toggle("hidden", this.index === this.stepTargets.length - 1)
    this.submitTarget.classList.toggle("hidden", this.index !== this.stepTargets.length - 1)
    if (scroll) this.stepTargets[this.index]?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  private stepIsValid(step: HTMLElement) {
    return this.validatableFields(step).every((field) => field.checkValidity())
  }

  private validateStep(step: HTMLElement) {
    const fields = this.validatableFields(step)
    const invalidFields = fields.filter((field) => !field.checkValidity())
    this.renderValidationErrors(step, fields, invalidFields)
    invalidFields[0]?.focus()
    return invalidFields.length === 0
  }

  private validatableFields(step: HTMLElement) {
    const radioNames = new Set<string>()
    return Array.from(step.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      "input:not([disabled]), select:not([disabled]), textarea:not([disabled])"
    )).filter((field) => {
      if (!(field instanceof HTMLInputElement) || field.type !== "radio") return true
      if (radioNames.has(field.name)) return false

      radioNames.add(field.name)
      return true
    })
  }

  // 未入力の必須項目は、ブラウザ既定文ではなくサーバー側（Railsのerrors）と同じ
  // 「項目名を入力してください」で表示する
  private fieldMessage(field: ValidatableField) {
    if (!field.validity.valueMissing) return field.validationMessage

    const selectable = field instanceof HTMLSelectElement ||
      (field instanceof HTMLInputElement && (field.type === "radio" || field.type === "checkbox"))
    return `${this.validationLabel(field)}を${selectable ? "選択" : "入力"}してください`
  }

  private validationLabel(field: ValidatableField) {
    const label = this.element.querySelector<HTMLLabelElement>(`label[for="${CSS.escape(field.id)}"]`)
    // ラベル末尾の「（必須）」「（円/坪）」などの補足は文に混ぜると冗長なため落とす
    return field.dataset.validationLabel ||
      label?.textContent?.trim().replace(/（[^（）]*）$/, "") ||
      field.name
  }

  private renderValidationErrors(
    step: HTMLElement,
    fields: ValidatableField[],
    invalidFields: ValidatableField[]
  ) {
    fields.forEach((field) => {
      // ラジオは同名グループでエラー欄を共有するため、id ではなく name 起点で引く
      // （id 起点だと候補1件目の行内に新しいエラー欄が差し込まれてしまう）
      const isRadio = field instanceof HTMLInputElement && field.type === "radio"
      const errorId = `${isRadio ? field.name : field.id}_error`
      let error = step.querySelector<HTMLElement>(`#${CSS.escape(errorId)}`)
      if (!error && !field.checkValidity()) {
        error = document.createElement("p")
        error.id = errorId
        error.dataset.fieldError = ""
        error.className = "mt-1 text-sm text-danger"
        field.insertAdjacentElement("afterend", error)
      }

      if (error) {
        error.textContent = this.fieldMessage(field)
        error.classList.toggle("hidden", field.checkValidity())
      }
      field.setAttribute("aria-invalid", String(!field.checkValidity()))
      if (!field.checkValidity()) field.setAttribute("aria-describedby", errorId)
      else field.removeAttribute("aria-describedby")
    })

    const summary = step.querySelector<HTMLElement>("[data-validation-summary]")
    const count = summary?.querySelector<HTMLElement>("[data-validation-error-count]")
    const list = summary?.querySelector<HTMLElement>("[data-validation-error-list]")
    if (summary && count && list) {
      count.textContent = String(invalidFields.length)
      list.replaceChildren(...invalidFields.map((field) => {
        const item = document.createElement("li")
        item.textContent = field.validity.valueMissing
          ? this.fieldMessage(field)
          : `${this.validationLabel(field)}: ${field.validationMessage}`
        return item
      }))
      summary.classList.toggle("hidden", invalidFields.length === 0)
    }
  }
}
